import React, { useEffect, useRef, useState } from 'react';
import './CyberpunkDashboard.css'; // We'll extract the CSS separately

const CyberpunkDashboard: React.FC = () => {
  const [time, setTime] = useState('');
  const [date, setDate] = useState('');
  const outerRingRef = useRef<HTMLDivElement>(null);
  const middleRingRef = useRef<HTMLDivElement>(null);
  const innerRingRef = useRef<HTMLDivElement>(null);

  // Update time and date
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      setTime(`${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`);
      setDate(`${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`);
    };

    updateDateTime();
    const interval = setInterval(updateDateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Circle animations
  useEffect(() => {
    let animationFrameId: number;
    let startTime: number | null = null;
    const duration = 10000; // 10 seconds for a complete cycle

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = (elapsed % duration) / duration;

      if (outerRingRef.current) {
        const rotation = progress * 360;
        const x = Math.sin(progress * Math.PI * 2) * 10;
        const y = Math.cos(progress * Math.PI * 2) * 10;
        outerRingRef.current.style.transform = `translate(${x}px, ${y}px) rotate(${rotation}deg)`;
      }

      if (middleRingRef.current) {
        const rotation = -progress * 720;
        const x = Math.sin(progress * Math.PI * 2 + Math.PI/3) * 15;
        const y = Math.cos(progress * Math.PI * 2 + Math.PI/3) * 15;
        middleRingRef.current.style.transform = `translate(${x}px, ${y}px) rotate(${rotation}deg)`;
      }

      if (innerRingRef.current) {
        const rotation = progress * 180;
        const x = Math.sin(progress * Math.PI * 2 + Math.PI/1.5) * 20;
        const y = Math.cos(progress * Math.PI * 2 + Math.PI/1.5) * 20;
        innerRingRef.current.style.transform = `translate(${x}px, ${y}px) rotate(${rotation}deg)`;
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  // Animated data points
  const DataPoint: React.FC<{ id: string; base: number; range: number; speed: number }> = ({ id, base, range, speed }) => {
    const [value, setValue] = useState(base.toString());

    useEffect(() => {
      const interval = setInterval(() => {
        const variation = (Math.random() - 0.5) * range;
        const newValue = base + variation;
        setValue(Math.round(newValue).toString());
      }, speed);

      return () => clearInterval(interval);
    }, [base, range, speed]);

    return <div className="data-point">{value}</div>;
  };

  return (
    <div className="dashboard">
      <div className="grid-overlay"></div>
      
      <div className="corner-brackets top-left"></div>
      <div className="corner-brackets top-right"></div>
      <div className="corner-brackets bottom-left"></div>
      <div className="corner-brackets bottom-right"></div>
      
      <div className="header">CÔNG THỨC TÍNH BCR</div>
      
      <div className="time-display">{time}</div>
      <div className="date-display">{date}</div>
      <div className="security-label">Security</div>
      
      <div className="left-panel">
        <DataPoint id="data1" base={272} range={100} speed={150} />
        <DataPoint id="data2" base={283} range={80} speed={200} />
        <DataPoint id="data3" base={471} range={120} speed={180} />
      </div>
      
      <div className="bar-chart">
        {[60, 80, 45, 90, 35, 70, 55, 85].map((height, index) => (
          <div 
            key={index}
            className="bar" 
            style={{
              height: `${height}%`,
              animationDelay: `${index * 0.2}s`
            }}
          ></div>
        ))}
      </div>
      
      <div className="central-circle">
        <div className="outer-ring" ref={outerRingRef}></div>
        <div className="middle-ring" ref={middleRingRef}></div>
        <div className="inner-ring" ref={innerRingRef}></div>
        
        <div className="center-logo">
          <div className="logo-x">✕</div>
        </div>
      </div>
      
      <NumericData id="topData" base={5091457.37} range={50000} speed={100} className="top-right" />
      <NumericData id="bottomData" base={8091457.37} range={80000} speed={120} className="bottom-right" />
      <NumericData id="centerData" base={6089} range={500} speed={160} className="center-left" />
    </div>
  );
};

const NumericData: React.FC<{
  id: string;
  base: number;
  range: number;
  speed: number;
  className: string;
}> = ({ id, base, range, speed, className }) => {
  const [value, setValue] = useState(base.toFixed(2));

  useEffect(() => {
    const interval = setInterval(() => {
      const variation = (Math.random() - 0.5) * range;
      const newValue = base + variation;
      setValue(newValue.toFixed(2));
    }, speed);

    return () => clearInterval(interval);
  }, [base, range, speed]);

  return <div className={`numeric-data ${className}`}>{value}</div>;
};

export default CyberpunkDashboard;